'use client';
import { useState, FormEvent, useEffect } from 'react';
import { useStripe, useElements, CardElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import type { PaymentIntent, PaymentRequest } from '@stripe/stripe-js';

interface PaymentFormProps {
  clientSecret: string;
  billingInfo: {
    firstName: string;
    lastName: string;
    email: string;
    addressLine1: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  onSuccess: (paymentIntent: PaymentIntent) => void;
  onError: (error: string) => void;
}

export default function PaymentForm({ clientSecret, billingInfo, onSuccess, onError }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);

  console.log('=== PAYMENT FORM MOUNTED ===');
  console.log('Client secret received:', clientSecret?.substring(0, 20) + '...');
  console.log('Client secret format valid:', clientSecret?.startsWith('pi_') && clientSecret?.includes('_secret_'));
  console.log('PaymentIntent ID from secret:', clientSecret?.split('_secret_')[0]);
  console.log('Stripe loaded:', !!stripe);
  console.log('Elements loaded:', !!elements);
  console.log('Billing postal code:', billingInfo.postalCode);

  // Initialize Payment Request Button for Apple Pay / Google Pay
  useEffect(() => {
    if (stripe && clientSecret) {
      console.log('\n=== INITIALIZING PAYMENT REQUEST BUTTON ===');
      const pr = stripe.paymentRequest({
        country: 'GB',
        currency: 'gbp',
        total: {
          label: 'LUME001',
          amount: 3888, // £38.88 in pence
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          console.log('✅ Digital wallets available:', result);
          setPaymentRequest(pr);
        } else {
          console.log('ℹ️ No digital wallets available on this device');
        }
      });

      pr.on('paymentmethod', async (ev) => {
        console.log('\n=== DIGITAL WALLET PAYMENT INITIATED ===');
        console.log('Payment method type:', ev.paymentMethod.type);
        console.log('Payment method ID:', ev.paymentMethod.id);
        
        try {
          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
            clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          );

          if (confirmError) {
            console.error('❌ Digital wallet payment failed:', confirmError.message);
            ev.complete('fail');
            onError(confirmError.message || 'Payment failed');
          } else if (paymentIntent) {
            if (paymentIntent.status === 'requires_action') {
              console.log('Payment requires additional action');
              const { error, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(clientSecret);
              if (error) {
                console.error('❌ Payment action failed:', error.message);
                ev.complete('fail');
                onError(error.message || 'Payment failed');
              } else if (confirmedIntent) {
                console.log('✅ Digital wallet payment succeeded:', confirmedIntent.id);
                ev.complete('success');
                onSuccess(confirmedIntent);
              }
            } else {
              console.log('✅ Digital wallet payment succeeded:', paymentIntent.id);
              ev.complete('success');
              onSuccess(paymentIntent);
            }
          }
        } catch (err) {
          console.error('❌ Digital wallet payment error:', err);
          ev.complete('fail');
          onError('Payment failed. Please try again.');
        }
      });
    }
  }, [stripe, clientSecret, onSuccess, onError]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    console.log('=== PAYMENT SUBMISSION ===');
    console.log('Stripe ready:', !!stripe);
    console.log('Elements ready:', !!elements);
    console.log('Card complete:', isCardComplete);
    console.log('Client secret being used:', clientSecret?.substring(0, 20) + '...');
    console.log('PaymentIntent ID:', clientSecret?.split('_secret_')[0]);
    
    if (!stripe || !elements) {
      console.error('❌ Stripe or elements not loaded');
      onError('Payment system not initialized. Please refresh and try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      console.error('❌ CardElement not found');
      onError('Payment form not ready. Please refresh and try again.');
      return;
    }

    if (!isCardComplete) {
      console.error('❌ Card details incomplete');
      onError('Please complete all card details.');
      return;
    }
    
    setIsProcessing(true);
    console.log('Processing payment with postal code:', billingInfo.postalCode);
    console.log('Confirming payment with clientSecret:', clientSecret.substring(0, 30) + '...');

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${billingInfo.firstName} ${billingInfo.lastName}`,
            email: billingInfo.email,
            address: {
              line1: billingInfo.addressLine1,
              city: billingInfo.city,
              state: billingInfo.state,
              postal_code: billingInfo.postalCode,
              country: billingInfo.country,
            },
          },
        },
      });

      if (error) {
        console.error('❌ Payment confirmation failed:', error);
        console.error('Error type:', error.type);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        let userMessage = error.message || 'Payment failed. Please try again.';
        
        if (error.type === 'card_error') {
          userMessage = `Card Error: ${error.message}`;
        } else if (error.type === 'validation_error') {
          userMessage = 'Please check your payment details and try again.';
        }
        
        onError(userMessage);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded:', paymentIntent.id);
        onSuccess(paymentIntent);
      } else {
        console.warn('⚠️ Unexpected payment status:', paymentIntent?.status || 'undefined');
        onError('Payment status unclear. Please check your email for confirmation.');
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('❌ Payment error:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'An unexpected error occurred. Please try again.';
      onError(errorMessage);
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#3C3C3C',
        fontFamily: 'system-ui, sans-serif',
        '::placeholder': {
          color: '#9CA3AF',
        },
        backgroundColor: '#F5F1E8',
      },
      invalid: {
        color: '#EF4444',
        iconColor: '#EF4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      {/* Payment Request Button (Apple Pay / Google Pay) */}
      {paymentRequest && (
        <div className="pb-3 sm:pb-4 border-b border-foreground/10">
          <PaymentRequestButtonElement options={{ paymentRequest }} />
          <p className="text-xs text-foreground/50 text-center mt-2">
            Or pay with card below
          </p>
        </div>
      )}

      <div className="p-3 sm:p-4 bg-[#F5F1E8] border-2 border-[#5C4033] rounded-lg">
        <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2 sm:mb-3">
          Card Details
        </label>
        <CardElement
          options={cardElementOptions}
          onChange={(e) => {
            console.log('Card element changed:', e.complete, e.error);
            setIsCardComplete(e.complete);
            if (e.error) {
              console.error('Card validation error:', e.error.message);
            }
          }}
          onReady={() => {
            console.log('✅ CardElement is ready');
          }}
        />
      </div>
      
      <button
        type="submit"
        disabled={!stripe || !isCardComplete || isProcessing}
        className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-[#8B9D83] text-white mono-data text-xs sm:text-sm uppercase tracking-wider rounded-lg hover:bg-[#7A8C73] transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
      >
        {isProcessing ? 'PROCESSING...' : 'COMPLETE PAYMENT'}
      </button>
      
      <p className="text-xs text-foreground/50 text-center">
        Your payment is secured by Stripe. We never store your card details.
      </p>
    </form>
  );
}